import useSWR from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';

export type WorkspaceStatus = 'draft' | 'in_review' | 'completed' | 'closed';

export interface Workspace {
  id: string;
  repository_id: string;
  user_id: string;
  name: string;
  branch: string;
  is_draft: boolean;
  creator_name: string;
  sandbox_id: string | null;
  sandbox_status: string;
  snapshot_override: string | null;
  status: WorkspaceStatus;
  ai_complexity_type: string | null;
  ai_trust_percentage: number | null;
  tracker_issue_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface GitBranch {
  name: string;
  is_default: boolean;
}

interface ListWorkspacesResponse {
  workspaces: Workspace[];
}

export function useWorkspaces(repositoryId: string | undefined) {
  const { data, mutate, isLoading } = useSWR(
    repositoryId ? `/api/v1/repositories/${repositoryId}/workspaces` : null,
    () => fetchEndpointData<ListWorkspacesResponse>(`/api/v1/repositories/${repositoryId}/workspaces`),
  );
  return { workspaces: data?.workspaces ?? [], mutate, isLoading };
}

export function useBranches(repositoryId: string | undefined, enabled: boolean) {
  const { data, isLoading, error } = useSWR(
    enabled && repositoryId ? `/api/v1/repositories/${repositoryId}/branches` : null,
    () => fetchEndpointData<{ branches: GitBranch[] }>(`/api/v1/repositories/${repositoryId}/branches`),
  );
  return { branches: data?.branches ?? [], isLoading, error };
}

export async function createWorkspace(
  repositoryId: string,
  body: {
    name: string;
    branch?: string;
    snapshot_override?: string;
    ai_complexity_type?: string;
    ai_trust_percentage?: number;
    tracker_issue_id?: string;
  },
) {
  return fetchEndpointData(`/api/v1/repositories/${repositoryId}/workspaces`, { method: 'POST', body });
}

export async function deleteWorkspace(repositoryId: string, workspaceId: string) {
  return fetchEndpointData(`/api/v1/repositories/${repositoryId}/workspaces/${workspaceId}`, { method: 'DELETE' });
}

export async function updateWorkspaceStatus(repositoryId: string, workspaceId: string, status: WorkspaceStatus) {
  return fetchEndpointData(`/api/v1/repositories/${repositoryId}/workspaces/${workspaceId}/status`, {
    method: 'PUT',
    body: { status },
  });
}
